<!--eslint-disable vue/no-v-html-->
<template>
    <div class="row gx-3">
        <pc-label
            v-if="label"
            v-bind:label="label"
            class="mb-2 col-12"
            v-bind:class="underCaptionClass" />
        <div
            class="col-12 d-flex flex-row"
            v-bind:class="{
                'justify-content-center': centerAligned,
                'justify-content-start': useAutoSizing && leftAligned,
                'justify-content-end': useAutoSizing && rightAligned
            }">
            <div
                v-if="rightAligned && sideUsable"
                class="flex-grow-1 me-3"
                v-html="imageCaption" />
            <div
                ref="imgWrapper"
                v-bind:class="imageClass"
                class="p-0">
                <div v-if="showImagePlaceholder">
                    <pc-bs-icon
                        name="image"
                        style="font-size: 32px"
                        class="me-2" />
                    <span>{{ imagePlaceholderText }}</span>
                </div>
                <div v-else-if="Boolean(imageUrl)">
                    <a
                        v-if="imageHref"
                        v-bind:href="imageHref"
                        target="_blank"
                        class="w-100">
                        <img
                            v-bind:src="imageUrl"
                            v-bind:alt="imageAltText"
                            class="w-100" />
                    </a>
                    <img
                        v-else
                        v-bind:src="imageUrl"
                        v-bind:alt="imageAltText"
                        class="w-100" />
                </div>
            </div>
            <div
                v-if="leftAligned && sideUsable"
                class="flex-grow-1 ms-3"
                v-html="imageCaption" />
        </div>
        <div
            v-if="imageCaption && (centerAligned || !sideUsable)"
            class="col-12 mt-2"
            v-bind:class="underCaptionClass"
            v-html="imageCaption" />
    </div>
</template>
<script>
    import base from "./_elementBase";
    import { isModuleRegistered } from "SharedStores/util";
    import { EDIT_FORM_NAMESPACE } from "Forms/EditForm/editFormStoreDefinitions";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/image.i18n";
    import { responsiveManager } from "Responsive/manager";
    import { PROJECT_NAMESPACE } from "SharedStores/Project/projectStore";

    const sizes = {
        full: "12",
        half: "6",
        quarter: "3",
        icon: "1",
        native: "0"
    };

    const positions = {
        left: "left",
        right: "right",
        center: "center"
    };

    const POLL_INTERVAL = 2000;
    const MAXIMUM_POLL_COUNT = 1800000 / POLL_INTERVAL; // 30 minutes

    export default {
        extends: base,
        inject: {
            anonParams: {
                from: "anonParams",
                default: null
            }
        },
        data() {
            return {
                imageUrl: null,
                attachmentPollTimeout: null,
                currentPollCount: 0
            };
        },
        computed: {
            imageId() {
                return this.element.Properties.contentitemid;
            },
            imageHref() {
                return this.element.Properties.href;
            },
            imageIsPublic() {
                return this.element.Properties.contentispublic === "True";
            },
            imageAltText() {
                return this.element.Properties.alttext;
            },
            imageCaption() {
                return this.element.Properties.caption;
            },
            imageSourceIsAttachment() {
                return this.element.Properties.contentpath == "Attachments";
            },
            imageSizeOnPage() {
                return this.element.Properties.sizeonpage;
            },
            imagePosition() {
                return this.element.Properties.position;
            },
            // Serves as a "collector" that groups related properties together for the purpose of
            // watching them as a unit.
            imageDetails() {
                return {
                    id: this.imageId,
                    href: this.imageHref,
                    isPublic: this.imageIsPublic,
                    altText: this.imageAltText,
                    caption: this.imageCaption,
                    sourceIsAttachment: this.imageSourceIsAttachment,
                    sizeOnPage: this.imageSizeOnPage,
                    position: this.imagePosition
                };
            },
            contentLibraryController() {
                return this.$serviceControllers.ContentLibrary;
            },
            attachmentsController() {
                return this.$serviceControllers.Attachments;
            },
            onEditForm() {
                return isModuleRegistered(EDIT_FORM_NAMESPACE, this.$store.state);
            },
            showImagePlaceholder() {
                if (!this.onEditForm) {
                    return false;
                }

                return Boolean(!this.imageId || this.imageSourceIsAttachment);
            },
            imagePlaceholderText() {
                return this.imageSourceIsAttachment ? this.attachmentPlaceholderText : this.noImageSelectedText;
            },
            canFetchImage() {
                if (this.isOnEditForm && this.imageSourceIsAttachment) {
                    return false;
                }

                const requiredController = this.imageSourceIsAttachment
                    ? this.attachmentsController
                    : this.contentLibraryController;
                return Boolean(requiredController && this.imageId);
            },
            useAutoSizing() {
                return this.imageSizeOnPage == sizes.native;
            },
            imageClass() {
                const sop = this.imageSizeOnPage;
                return {
                    "col-12": sop == sizes.full,
                    "col-6": sop == sizes.half,
                    "col-3": sop == sizes.quarter,
                    "col-1": sop == sizes.icon
                };
            },
            sideUsable() {
                switch (this.imageSizeOnPage) {
                    case sizes.full:
                    case sizes.native:
                        return false;
                    default:
                        return true;
                }
            },
            leftAligned() {
                return this.imagePosition == positions.left;
            },
            rightAligned() {
                return this.imagePosition == positions.right;
            },
            centerAligned() {
                return this.imagePosition == positions.center;
            },
            underCaptionClass() {
                return {
                    "text-center": this.centerAligned,
                    "text-end": this.rightAligned
                };
            },
            ...mapI18n(KEYS, NAMESPACE)
        },
        watch: {
            imageDetails: {
                handler() {
                    if (!this.imageId) {
                        this.imageUrl = null;
                        this.stopPollingAttachment();
                    } else {
                        this.getSizedImageUrl();
                    }
                },
                deep: true
            }
        },
        mounted() {
            this.getSizedImageUrl();
            if (this.viewport) {
                this.viewport.addUpdatedListener(this.getSizedImageUrl);
            } else {
                responsiveManager.addResizeListener(this.getSizedImageUrl);
            }
        },
        beforeUnmount() {
            if (this.viewport) {
                this.viewport.removeUpdatedListener(this.getSizedImageUrl);
            } else {
                responsiveManager.removeResizeListener(this.getSizedImageUrl);
            }
            this.stopPollingAttachment();
        },
        methods: {
            async getSizedImageUrl() {
                if (!this.imageSourceIsAttachment) {
                    this.stopPollingAttachment();
                }

                if (!this.canFetchImage) {
                    return;
                }

                if (this.imageSourceIsAttachment) {
                    this.imageUrl = await this.fetchAttachmentUrl();
                    this.startPollingAttachment();
                    return;
                }

                this.stopPollingAttachment();

                this.imageUrl = this.buildContentLibraryUrl();
            },
            buildContentLibraryUrl() {
                if (!this.$refs.imgWrapper) {
                    return;
                }

                //Reference image by ID instead of path to resolve renames
                const libraryBase = this.imageIsPublic
                    ? this.contentLibraryController.PublicLibraryUrl
                    : this.contentLibraryController.InternalLibraryUrl;
                const baseUrl = libraryBase + "/ID_" + this.imageId;

                return this.appendParams(baseUrl);
            },
            async fetchAttachmentUrl() {
                // Attachments uploaded via the PowerClerk API aren't tracked in the store. Therefore,
                // we poll the project's attachments to check if our target exists.
                const {
                    Data: { Url: baseUrl }
                } = await this.attachmentsController.ViewLatestAttachmentOfDefinition({
                    programId: this.attachmentsController.ProgramId,
                    projectId: this.$store.state[PROJECT_NAMESPACE].ProjectId,
                    attachmentDefinitionId: this.imageId
                });

                if (!baseUrl) {
                    // No attachment has been uploaded yet.
                    return;
                }

                return this.appendParams(baseUrl);
            },
            appendParams(baseUrl) {
                let url = new URL(baseUrl);

                if (!this.useAutoSizing) {
                    let size = this.$refs.imgWrapper.clientWidth;
                    if (size >= 2048) {
                        size = 2048;
                    } else if (size <= 64) {
                        size = 64;
                    } else {
                        size = Math.pow(2, Math.ceil(Math.log2(size)));
                    }
                    url.searchParams.append("Size", size);
                } else {
                    url.searchParams.append("Size", 0);
                }

                if (url.hostname === "localhost" && !url.searchParams.has("ProgramId")) {
                    url.searchParams.append("ProgramId", this.$app.ProgramId);
                }

                url = url.toString();

                if (this.anonParams) {
                    url += this.anonParams;
                }

                return url;
            },
            startPollingAttachment() {
                if (this.attachmentPollTimeout) {
                    return;
                }

                const poll = async () => {
                    if (this.currentPollCount >= MAXIMUM_POLL_COUNT) {
                        this.stopPollingAttachment();
                        return;
                    }

                    this.imageUrl = await this.fetchAttachmentUrl();
                    this.currentPollCount++;
                    this.attachmentPollTimeout = setTimeout(poll, POLL_INTERVAL);
                };

                poll();
            },
            stopPollingAttachment() {
                if (this.attachmentPollTimeout) {
                    clearTimeout(this.attachmentPollTimeout);
                    this.attachmentPollTimeout = null;
                    this.currentPollCount = 0;
                }
            }
        }
    };
</script>
